import { useSignal } from "@preact/signals";
import { navigateTo } from "bunfx";
import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { rpc } from "../../rpc";
import { currentUser, setCurrentUser } from "../../stores/current-user";
import { showError } from "../../stores/errors";
import { type MimicState, mimicState, setMimicState } from "../../stores/mimic";
import type { User } from "../../types";
import {
  IcoChevronLeft,
  IcoLogo,
  IcoSignOut,
  IcoUser,
  IcoUsers,
} from "../icons";
import { ContentCard, ContentCardBody } from "../ui/content-card";
import { ErrorDialog } from "../ui/error-dialog";
import { MimicBanner } from "../ui/mimic-banner";
import { NotificationBell } from "../ui/notification-bell";

type AccountLayoutProps = {
  children: ComponentChildren;
  user: User;
  mimic?: MimicState;
  title: string;
};

/**
 * Layout for account-level pages (settings, profile, etc.)
 * Uses a neutral brand color with the same stacked card pattern as workspace pages.
 */
export function AccountLayout({
  children,
  user,
  mimic,
  title,
}: AccountLayoutProps) {
  const menuOpen = useSignal(false);

  // Initialize the global currentUser and mimic signals from the page's props
  useEffect(() => {
    setCurrentUser(user);
    setMimicState(mimic ?? null);
  }, [user, mimic]);

  // Close menu when clicking outside or on a link/button
  useEffect(() => {
    if (!menuOpen.value) return;

    const onDocClick = (e: MouseEvent) => {
      const target = (e.target as Element).closest("a,button,[role=menu]");
      if (!target || target.matches("a,button")) {
        menuOpen.value = false;
      }
    };

    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [menuOpen.value]);

  async function handleLogout() {
    try {
      await rpc.auth.logout({});
      navigateTo("/login");
    } catch {
      showError("Logout failed", "Please try again.");
    }
  }

  const isMimicking = mimicState.value !== null;

  return (
    <div class={`min-h-screen bg-surface-page ${isMimicking ? "pt-10" : ""}`}>
      <MimicBanner />
      <ErrorDialog />

      {/* Header with neutral brand band */}
      <header
        data-brand="slate"
        class="bg-gradient-to-r from-[oklch(0.35_0.18_250)] to-[oklch(0.50_0.20_250)]"
      >
        <div class="px-4 lg:px-8">
          <div class="flex items-center justify-between h-16">
            <div class="flex items-center gap-4">
              <a
                href="/"
                class="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
              >
                <IcoLogo class="size-8" />
                <span class="text-xl font-semibold">Pulse</span>
              </a>
            </div>

            <div class="flex items-center gap-2">
              <NotificationBell />

              <div class="relative leading-0">
                <button
                  type="button"
                  class="rounded-full hover:ring-2 hover:ring-white/20 transition-shadow"
                  aria-expanded={menuOpen.value}
                  aria-haspopup="menu"
                  aria-label="User menu"
                  onClick={() => {
                    menuOpen.value = !menuOpen.value;
                  }}
                >
                  <UserAvatar />
                </button>

                {menuOpen.value && (
                  <div
                    class="absolute right-0 top-full mt-2 w-64 rounded-xl bg-surface-raised shadow-xl ring-1 ring-border-muted p-2 z-50"
                    role="menu"
                    aria-label="User menu"
                  >
                    <div class="flex items-center gap-3 px-3 py-3 border-b border-border-muted mb-2">
                      <UserAvatar />
                      <div class="flex flex-col min-w-0">
                        <span class="text-sm font-medium text-text truncate">
                          {currentUser.value?.name}
                        </span>
                        <span class="text-xs text-text-muted truncate">
                          {currentUser.value?.email}
                        </span>
                      </div>
                    </div>
                    <a
                      href="/account"
                      class="flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-surface-active hover:text-text rounded-lg transition-colors"
                      role="menuitem"
                    >
                      <IcoUser class="size-5 text-text-subtle" />
                      Account
                    </a>
                    <a
                      href="/orgs"
                      class="flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-surface-active hover:text-text rounded-lg transition-colors"
                      role="menuitem"
                    >
                      <IcoUsers class="size-5 text-text-subtle" />
                      Organizations
                    </a>
                    <button
                      type="button"
                      onClick={handleLogout}
                      class="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-surface-active hover:text-text rounded-lg transition-colors"
                      role="menuitem"
                    >
                      <IcoSignOut class="size-5 text-text-subtle" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Title section with back link and page title */}
        <div class="px-4 lg:px-8 pb-16">
          <a
            href="/"
            class="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors mb-3"
          >
            <IcoChevronLeft class="size-4" />
            Back to home
          </a>
          <h1 class="text-2xl font-bold text-white">{title}</h1>
        </div>
      </header>

      {/* Content card that overlaps into header */}
      <div class="px-4 lg:px-8 pb-8">
        <ContentCard>
          <ContentCardBody>{children}</ContentCardBody>
        </ContentCard>
      </div>
    </div>
  );
}

function UserAvatar() {
  const user = currentUser.value;
  const profilePictureUrl = user?.profilePictureUrl;

  return (
    <div class="size-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
      {profilePictureUrl ? (
        <img
          src={profilePictureUrl}
          alt={user?.name || "User"}
          class="size-full object-cover"
        />
      ) : (
        <IcoUser class="size-5 text-white/80" />
      )}
    </div>
  );
}
